import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";
import Navbar from "./components/Navbar/Navbar";
import Bot from "./components/Bot/Bot";
import NewsMarquee from "./components/Marquee/NewsMarquee";
import CompaniesMarquee from "./components/Marquee/CompaniesMarquee";
import MarketClock from "./components/Clock/MarketClock";
import Dashboard from "./components/Dashboard/Dashboard";
import AccountAccess from "./components/AccountAccess/AccountAccess";
import AccountOnboarding from "./components/AccountOnboarding/AccountOnboarding";
import AccountHub from "./components/AccountHub/AccountHub";
import SavvyLoader from "./components/SavvyLoader/SavvyLoader";
import ResearchDashboard from "./components/ResearchDashboard/ResearchDashboard";
import EngineeringDashboard from "./components/EngineeringDashboard/EngineeringDashboard";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { buildApiUrl } from "./lib/api";

axios.defaults.withCredentials = true;

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(
        localStorage.getItem("isLoggedIn") === "true"
    );
    const [botTrigger, setBotTrigger] = useState(0);
    const [currentUser, setCurrentUser] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(
        localStorage.getItem("savvy_active_view") === "onboarding"
    );
    const [appView, setAppView] = useState(
        localStorage.getItem("savvy_shell_view") || "account"
    );
    const [accessMode, setAccessMode] = useState("register");
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [showDashboardLoader, setShowDashboardLoader] = useState(
        isLoggedIn && !showOnboarding && (localStorage.getItem("savvy_shell_view") || "account") === "dashboard"
    );
    const [logoutGreeting, setLogoutGreeting] = useState(null);
    const siteHeaderRef = useRef(null);

    useEffect(() => {
        const el = siteHeaderRef.current;
        if (!el) return;
        const update = (h) =>
            document.documentElement.style.setProperty("--site-header-height", `${h}px`);
        update(el.getBoundingClientRect().height);
        const observer = new ResizeObserver(([entry]) => update(entry.contentRect.height));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const isDashboardHeaderStack = isLoggedIn && !showOnboarding && appView === "dashboard";
        document.body.classList.toggle("mobile-dashboard-header-stack", isDashboardHeaderStack);
        return () => {
            document.body.classList.remove("mobile-dashboard-header-stack");
        };
    }, [appView, isLoggedIn, showOnboarding]);

    useEffect(() => {
        if (isLoggedIn && !showOnboarding) {
            setBotTrigger((prev) => prev + 1);
        }
    }, [isLoggedIn, showOnboarding]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [appView, showOnboarding]);

    useEffect(() => {
        if (!isLoggedIn) {
            setCurrentUser(null);
            setShowOnboarding(false);
            localStorage.removeItem("savvy_active_view");
            return;
        }

        localStorage.setItem(
            "savvy_active_view",
            showOnboarding ? "onboarding" : "dashboard"
        );
        localStorage.setItem("savvy_shell_view", appView);
    }, [appView, isLoggedIn, showOnboarding]);

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        let isMounted = true;

        const loadCurrentUser = async () => {
            try {
                const session = await axios.get(buildApiUrl("/is_logged_in"), {
                    withCredentials: true,
                });
                if (!session?.data?.logged_in) {
                    if (isMounted) {
                        setCurrentUser(null);
                        setIsLoggedIn(false);
                        localStorage.removeItem("isLoggedIn");
                    }
                    return;
                }

                const { data } = await axios.get(buildApiUrl("/me"), {
                    withCredentials: true,
                });
                if (isMounted) {
                    setCurrentUser(data);
                    if (data?.email) {
                        localStorage.setItem("email", data.email);
                    }
                }
            } catch {
                if (isMounted) {
                    setCurrentUser(null);
                }
            }
        };

        loadCurrentUser();

        return () => {
            isMounted = false;
        };
    }, [isLoggedIn]);

    const handleAuthSuccess = (user, options = {}) => {
        if (user?.email) {
            localStorage.setItem("email", user.email);
        }
        localStorage.setItem("isLoggedIn", "true");
        setIsLoggedIn(true);
        setCurrentUser(user || null);
        setIsAuthModalOpen(false);
        setShowOnboarding(Boolean(options.openOnboarding));
        setAppView("account");
    };

    const handleOpenAccount = () => {
        if (isLoggedIn) {
            setShowOnboarding(true);
        } else {
            setAccessMode("register");
            setIsAuthModalOpen(true);
        }

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleOpenLogin = () => {
        setAccessMode("login");
        setIsAuthModalOpen(true);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleNavigateAccount = () => {
        setShowOnboarding(false);
        setAppView("account");
    };

    const handleNavigateDashboard = () => {
        setShowOnboarding(false);
        setAppView("dashboard");
        setShowDashboardLoader(true);
    };

    const handleNavigateResearch = () => {
        setShowOnboarding(false);
        setAppView("research");
    };

    const handleNavigateEngineering = () => {
        if (!currentUser?.is_local_admin) return;
        setShowOnboarding(false);
        setAppView("engineering");
    };

    const handleLogoutMessage = (msg) => {
        setLogoutGreeting(msg);
        setTimeout(() => setLogoutGreeting(null), 4500);
    };

    return (
        <>
            <div className="site-header" ref={siteHeaderRef}>
                <NewsMarquee />
                <AnimatePresence>
                    {isLoggedIn && !showOnboarding && appView === "dashboard" && (
                        <motion.div
                            key="companies-marquee"
                            initial={{ height: 0 }}
                            animate={{ height: "auto" }}
                            exit={{ height: 0 }}
                            transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
                            style={{ overflow: "hidden", background: "#000000" }}
                        >
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { duration: 0.7, delay: 0.3, ease: "easeIn" } }}
                                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                            >
                                <CompaniesMarquee />
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
                <Navbar
                    isLoggedIn={isLoggedIn}
                    setIsLoggedIn={setIsLoggedIn}
                    onOpenAccount={handleOpenAccount}
                    onOpenLogin={handleOpenLogin}
                    onNavigateAccount={handleNavigateAccount}
                    onNavigateDashboard={handleNavigateDashboard}
                    onNavigateResearch={handleNavigateResearch}
                    onNavigateEngineering={handleNavigateEngineering}
                    onLogoutMessage={handleLogoutMessage}
                    currentUser={currentUser}
                />
            </div>
            <div
                className={[
                    "fullscreen-background",
                    !isLoggedIn ? "fullscreen-background-guest" : "",
                    (isLoggedIn && !showOnboarding && appView === "dashboard") ? "fullscreen-background-dashboard-static" : "",
                ].filter(Boolean).join(" ")}
            >

            {(isLoggedIn && !showOnboarding) || logoutGreeting ? (
                <Bot key={botTrigger} isLoggedIn={isLoggedIn} forceMessage={logoutGreeting} />
            ) : null}

            <main className="app-main-shell">
                {isLoggedIn ? (
                    showOnboarding ? (
                        <AccountOnboarding
                            user={currentUser}
                            onExit={() => setShowOnboarding(false)}
                            onSubmitted={(user) => {
                                if (user) {
                                    setCurrentUser(user);
                                }
                                setShowOnboarding(false);
                                setAppView("account");
                            }}
                        />
                    ) : (
                        <AnimatePresence>
                            {appView === "dashboard" ? (
                                <motion.div
                                    key="dashboard"
                                    initial={{ opacity: 1 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <Dashboard
                                        isLocalDevMode={
                                            currentUser?.auth_provider === "local_dev"
                                            && !currentUser?.is_local_admin
                                        }
                                    />
                                </motion.div>
                            ) : appView === "research" ? (
                                <motion.div
                                    key="research"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <ResearchDashboard user={currentUser} />
                                </motion.div>
                            ) : appView === "engineering" && currentUser?.is_local_admin ? (
                                <motion.div
                                    key="engineering"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <EngineeringDashboard user={currentUser} />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="account"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <AccountHub
                                        user={currentUser}
                                        onResumeOnboarding={() => setShowOnboarding(true)}
                                        onOpenFunding={() => setAppView("account")}
                                        onOpenDashboard={() => { setAppView("dashboard"); setShowDashboardLoader(true); }}
                                        onUserUpdated={setCurrentUser}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )
                ) : null}
            </main>

            <AnimatePresence>
                {!isLoggedIn && isAuthModalOpen ? (
                    <motion.div
                        className="auth-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.24, ease: "easeOut" }}
                        onClick={() => setIsAuthModalOpen(false)}
                    >
                        <motion.div
                            className="auth-modal-stage"
                            initial={{ opacity: 0, y: 24, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 12, scale: 0.985 }}
                            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <AccountAccess
                                mode={accessMode}
                                onModeChange={setAccessMode}
                                onAuthSuccess={handleAuthSuccess}
                                onClose={() => setIsAuthModalOpen(false)}
                            />
                        </motion.div>
                    </motion.div>
                ) : null}
            </AnimatePresence>

            <ToastContainer
                position="bottom-center"
                autoClose={4000}
                theme="dark"
                hideProgressBar={false}
                closeOnClick
                pauseOnHover
            />

            <div className="wrapper">
                <MarketClock />
            </div>

            {showDashboardLoader ? (
                <SavvyLoader onDone={() => setShowDashboardLoader(false)} />
            ) : null}
        </div>
        </>
    );
}

export default App;
